import {
  array,
  excludes,
  type InferOutput,
  literal,
  maxLength,
  minLength,
  nonEmpty,
  object,
  pipe,
  regex,
  string,
  trim,
  union,
  url,
} from "valibot";

import { EmailPipeline } from "common/schemas";

export const ApiKeyNameSchema = object({
  name: pipe(
    string(),
    trim(),
    nonEmpty("Key name is required"),
    maxLength(254, "Key name must be 254 characters or less"),
  ),
});

export const WebhookFormSchema = object({
  name: pipe(
    string(),
    trim(),
    nonEmpty("Webhook name is required"),
    maxLength(254, "Webhook name must be 254 characters or less"),
  ),
  url: pipe(
    string(),
    nonEmpty("Webhook URL is required"),
    url("Enter a valid URL format (e.g., https://example.com)"),
  ),
  events: pipe(array(string()), nonEmpty("Select at least one event")),
});

export const ProtectedWebhookFormSchema = object({
  ...WebhookFormSchema.entries,
  headers: object({
    name: pipe(string(), trim(), nonEmpty("Header name is required")),
    value: pipe(string(), trim(), nonEmpty("Header value is required")),
  }),
});

export type WebhookFormType = InferOutput<
  typeof WebhookFormSchema | typeof ProtectedWebhookFormSchema
>;

export const FullNameSchema = object({
  firstName: pipe(
    string(),
    trim(),
    minLength(1, "First name is required"),
    maxLength(40, "First name can't be more than 40 characters."),
  ),
  lastName: pipe(
    string(),
    trim(),
    minLength(1, "Last name is required"),
    maxLength(40, "Last name can't be more than 40 characters."),
  ),
});

export const AgencyClientSchema = object({
  firstName: pipe(string(), trim(), minLength(1, "First name is required")),
  lastName: pipe(string(), trim(), minLength(1, "Last name is required")),
  email: EmailPipeline,
});

export type AgencyClientSchemaType = InferOutput<typeof AgencyClientSchema>;

/*whitelabel config schemas*/

export const AgencyNameSchema = object({
  name: pipe(
    string(),
    trim(),
    minLength(1, "Name is required"),
    maxLength(254, "Name must be 254 characters or less"),
  ),
});
export type AgencyNameSchemaType = InferOutput<typeof AgencyNameSchema>;

export const GTMIdSchema = object({
  gtm: pipe(
    string(),
    trim(),
    regex(
      /^GTM-\w{7,8}$|^$/,
      "Enter a valid GTM ID format (e.g., GTM-XXXXXXX)",
    ),
  ),
});
export type GTMIdSchemaType = InferOutput<typeof GTMIdSchema>;

export const WhitelabelSlugSchema = object({
  slug: pipe(
    string(),
    trim(),
    minLength(1, "Subdomain is required"),
    maxLength(16, "Subdomain must be 16 characters or less"),
    regex(
      /^[a-z0-9-]*$/,
      "Invalid subdomain, only lowercase letters, numbers and hyphens are allowed",
    ),
    regex(
      /^[a-z0-9].*[a-z0-9]$/,
      "Subdomain must end and start with a letter or a number",
    ),
  ),
});
export type WhitelabelSlugSchemaType = InferOutput<typeof WhitelabelSlugSchema>;

export const AgencyDomainSchema = object({
  domainName: pipe(
    string(),
    trim(),
    minLength(1, "Domain is required"),
    excludes(
      import.meta.env.VITE_WHITE_LABEL_DOMAIN,
      `The domain ${import.meta.env.VITE_WHITE_LABEL_DOMAIN} is reserved, please use a custom domain`,
    ),
    regex(
      /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/,
      "Enter a valid domain format (e.g., domain.lnconnect.me)",
    ),
  ),
});
export type AgencyDomainSchemaType = InferOutput<typeof AgencyDomainSchema>;

export const CreateAgencyConfigSchema = object({
  ...AgencyNameSchema.entries,
  ...WhitelabelSlugSchema.entries,
});
export type CreateAgencyConfigSchemaType = InferOutput<
  typeof CreateAgencyConfigSchema
>;

export const ExtensionIDSchema = object({
  id: union([pipe(string(), trim()), literal("")]),
});
export type ExtensionIDSchemaType = InferOutput<typeof ExtensionIDSchema>;
